// @ts-check

import React, { useEffect, useMemo } from "react";
import { Banner, BlockStack, Layout, Page, Text } from "@shopify/polaris";
import { useNavigate, useParams } from "react-router-dom";
import { keepPreviousData } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";

import { FetchOrdersControls } from "components/FetchOrderControls/FetchOrdersControls";
import { AdPropertyStats } from "components/AdPropertyStats/AdPropertyStats";

import { useGlobalContext } from "context/GlobalContext";

import { useAppQuery } from "hooks/use-app-query";
import { useFormatCurrency } from "hooks/use-format-currency";

export default function AdViewPage() {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { id } = useParams();
  const { dates } = useGlobalContext();
  const formatCurrency = useFormatCurrency();

  useEffect(() => {
    posthog.capture("$pageview");
  }, []);

  const {
    data: adResponse,
    refetch: refetchAd,
    isLoading: isLoadingAd,
    isFetching: isFetchingAd,
    isRefetching: isRefetchingAd,
  } = useAppQuery({
    url: `/api/ads/${id}`,
    extraQueryKeys: ["ads"],
    reactQueryOptions: {
      enabled: dates !== null,
      staleTime: 10 * 60 * 1000,
      placeholderData: keepPreviousData,
    },
  });

  const isInitialLoadingAd = isLoadingAd || (isFetchingAd && !adResponse);

  const adStatsUrl = useMemo(() => {
    const params = {
      browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (dates) {
      params.start_date = dates.start.toISOString();
      params.end_date = dates.end.toISOString();
    }

    return `/api/ads/${id}/stats?${new URLSearchParams(params).toString()}`;
  }, [dates]);

  const {
    data: adStatsResponse,
    refetch: refetchAdStats,
    isLoading: isLoadingAdStats,
    isFetching: isFetchingAdStats,
    isRefetching: isRefetchingAdStats,
  } = useAppQuery({
    url: adStatsUrl,
    extraQueryKeys: ["ads"],
    reactQueryOptions: {
      enabled: dates !== null,
      staleTime: 10 * 60 * 1000,
      placeholderData: keepPreviousData,
    },
  });

  const isInitialLoadingAdStats = isLoadingAdStats || (isFetchingAdStats && !adStatsResponse);

  const ad = adResponse?.ad;
  const adStats = adStatsResponse?.adStats || {};
  const totalAdStats = adStatsResponse?.totalAdStats || {};

  const handleRefresh = () => {
    refetchAd();
    refetchAdStats();
  };

  let title;
  if (isInitialLoadingAd) {
    title = "Loading...";
  } else if (ad) {
    title = ad.name;
  }

  let banner;
  if (
    (adStatsResponse?.error && !isRefetchingAdStats) ||
    (adResponse?.error && !isRefetchingAd)
  ) {
    banner = (
      <Layout.Section>
        <Banner
          tone="critical"
          title="Error"
          action={{
            content: "Contact support",
            onAction: () => {
              window.$crisp.push(["do", "chat:open"]);
            },
          }}
        >
          <Text as="p" variant="bodyMd">
            Error loading data, please refresh or contact support if the problem persists.
          </Text>
        </Banner>
      </Layout.Section>
    );
  }

  return (
    <Page
      title={title}
      // titleMetadata={status}
      subtitle={ad ? ad.ad_sets.ad_campaigns.platform_accounts.connected_platforms.connectable_platforms.name : undefined}
      backAction={{
        content: "Back to Ad Campaign",
        onAction: () => {
          if (ad.ad_set_id) {
            navigate(`/ad-sets/${ad.ad_set_id}`);
          }
        },
      }}
      fullWidth
    >
      <Layout>
        {banner}

        <Layout.Section>
          <BlockStack gap="400">
            <FetchOrdersControls
              onRefresh={handleRefresh}
              loading={isRefetchingAd || isRefetchingAdStats}
            />

            <AdPropertyStats
              loading={isInitialLoadingAdStats}
              totalSpend={totalAdStats.total_spend ? formatCurrency(totalAdStats.total_spend) : formatCurrency(0)}
              totalRevenue={totalAdStats.total_revenue ? formatCurrency(totalAdStats.total_revenue) : formatCurrency(0)}
              totalClicks={totalAdStats.total_clicks || 0}
              totalOrders={totalAdStats.total_orders || 0}
              roas={totalAdStats.avg_roas ? `${totalAdStats.avg_roas.toFixed(1)}x` : "0x"}
              cac={totalAdStats.avg_cac ? formatCurrency(totalAdStats.avg_cac) : formatCurrency(0)}
              aov={totalAdStats.avg_aov ? formatCurrency(totalAdStats.avg_aov) : formatCurrency(0)}
              impressions={totalAdStats.total_impressions || 0}
              conversionRate={totalAdStats.avg_conversion_rate ? `${(totalAdStats.avg_conversion_rate * 100).toFixed(1)}%` : "0%"}
              newCustomers={totalAdStats.total_new_customers || 0}
            />
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
